import Route from '@ember/routing/route';
import { type Registry as Services, service } from '@ember/service';
import { task, type TaskInstance } from 'ember-concurrency';
import type { RecommendationsModel } from 'lumen/routes/hotel/settings/recommendations';
import type { HotelAndMarketServiceWithHotel } from 'lumen/services/hotel-and-market';

export interface PriceRangeModel extends RecommendationsModel {
  ratePreferenceTaskResult: TaskInstance<void>;
}

export default class HotelSettingsRecommendationsPriceRangeRoute extends Route {
  @service declare alerting: Services['alerting'];
  @service('hotel-and-market')
  declare hotelAndMarketService: HotelAndMarketServiceWithHotel;
  @service declare intl: Services['intl'];
  @service
  declare pricingAssistantDataRequestService: Services['pricing-assistant-data-request-service'];

  saveStrategyTask = task({ drop: true }, async (strategyRecord) => {
    const parentModel = this.modelFor(
      'hotel.settings.recommendations',
    ) as RecommendationsModel;

    let isConfirmed = true;

    if (this.hotelAndMarketService.hotel.paAutopilotSettings?.['enabled']) {
      isConfirmed = await this.alerting.showWarningAlert({
        title: this.intl.t(
          'pricing_assistant.recommendations.settings.min_max_price.alert.title',
        ),
        text: this.intl.t(
          'pricing_assistant.recommendations.settings.min_max_price.alert.description',
        ),
        buttons: {
          cancel: {
            text: this.intl.t('general.cancel'),
          },
          confirm: {
            text: this.intl.t('general.save'),
          },
        },
      });
    }

    if (isConfirmed) {
      // eslint-disable-next-line @typescript-eslint/no-unsafe-argument
      return parentModel.saveStrategyTask.perform(strategyRecord);
    }
    return null;
  });

  model(): PriceRangeModel {
    // The price range component reads the preferred channel at render, so the
    // template gates on this task until it lands.
    const ratePreferenceTaskResult =
      this.pricingAssistantDataRequestService.loadPaRatePreferenceTask.perform(
        this.hotelAndMarketService.hotel.id!,
      );

    return {
      ...(this.modelFor(
        'hotel.settings.recommendations',
      ) as RecommendationsModel),
      ratePreferenceTaskResult,
      saveStrategyTask: this.saveStrategyTask,
    };
  }
}
